import { useMutation, useQueryClient } from "@tanstack/react-query";
import { DeleteIcon } from "lucide-react";
import { useState } from "react";
import { Heading } from "react-aria-components";

import { Button } from "@/components/base/button";
import { useAppForm } from "@/components/base/form";
import { Modal } from "@/components/base/modal";
import { title } from "@/components/base/primitives";
import {
  removeRefMutationOptions,
  removeRefSchema,
} from "@/functions/refs/remove-ref";
import { useTRPC } from "@/integrations/trpc/react";

export type RemoveRefFormProps = {
  tournamentDivisionId: number;
  ids?: number[];
  teamId?: number | null;
  poolMatchId?: number;
  playoffMatchId?: number;
};

export function RemoveRefForm(props: RemoveRefFormProps) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <Button
        variant="text"
        size="sm"
        color="primary"
        onPress={() => setOpen(true)}
        tooltip={"Remove Ref Team"}
      >
        <DeleteIcon size={16} />
      </Button>
      {open && (
        <RemoveRefFormContent {...props} isOpen={open} onOpenChange={setOpen} />
      )}
    </>
  );
}

function RemoveRefFormContent({
  ids,
  teamId,
  poolMatchId,
  playoffMatchId,
  isOpen,
  onOpenChange,
}: RemoveRefFormProps & {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const trpc = useTRPC();

  const { mutate, failureReason } = useMutation({
    ...removeRefMutationOptions(),
    onSuccess: () => {
      queryClient.invalidateQueries(
        trpc.matches.get.queryOptions({ poolMatchId, playoffMatchId }),
      );
      queryClient.invalidateQueries(trpc.tournaments.pathFilter());

      onOpenChange(false);
    },
  });

  const schema = removeRefSchema.omit({ ids: true, teamId: true });

  const form = useAppForm({
    defaultValues: {},
    validators: {
      onMount: schema,
      onChange: schema,
    },
    onSubmit: () => {
      mutate({
        ids,
        teamId,
      });
    },
  });

  return (
    <Modal isOpen={isOpen} onOpenChange={onOpenChange}>
      <div className="relative flex flex-col space-y-4 p-3">
        <Heading className={title({ size: "sm" })} slot="title">
          Remove Refs?
        </Heading>

        <p>Are you sure you want to remove this reffing assignment?</p>

        <form
          className="flex flex-col space-y-6"
          onSubmit={(e) => {
            e.preventDefault();

            form.handleSubmit();
          }}
        >
          {failureReason && (
            <form.AppForm>
              <form.Alert
                title={"Unable to mark abandoned ref"}
                description={failureReason.message}
              />
            </form.AppForm>
          )}

          <form.AppForm>
            <form.Footer>
              <Button onPress={() => onOpenChange(false)}>Cancel</Button>

              <form.SubmitButton requireChange={false}>
                Confirm
              </form.SubmitButton>
            </form.Footer>
          </form.AppForm>
        </form>
      </div>
    </Modal>
  );
}
